import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { RadioGroup } from '@mui/material'
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined'

import { REDACTION_SEARCH_TEXT_LENGTH, MAX_REDACTION_REASON_LENGTH } from 'citadel-shared/redactionLimits.js'
import { REDACTION_OCCURRENCE_ACTION, REDACTION_OCCURRENCE_ACTIONS } from 'citadel-shared/redactionEditing.js'

import { theme } from '../../../themes/theme'
import Button, { SecondaryButton } from '../../common/Button'
import { InfoMessage } from '../../common/UserMessage'
import { FormTitle, SmallBodyText, ErrorText } from '../../styles/Text'
import { DialogActionsRow, FlexDialogContent, StyledDialog } from '../../styles/Containers'
import { ControlledInput } from '../../styles/TextField'
import { Checkbox, FormControlLabel } from '../../styles/Checkbox'
import { StyledRadio, StyledFormControlLabel } from '../../styles/RadioGroup'
import { SECONDARY_TEXT } from '../../styles/colors'
import { getRedactionReasonPlaceholder } from '../../../util/redaction/redactionReasonPlaceholder'

const MIN_SEARCH_TEXT_LENGTH = REDACTION_SEARCH_TEXT_LENGTH.min
const MAX_SEARCH_TEXT_LENGTH = REDACTION_SEARCH_TEXT_LENGTH.max
const MAX_REASON_LENGTH = MAX_REDACTION_REASON_LENGTH

const Content = styled(FlexDialogContent)`
  padding: 30px;
  gap: 14px;
  width: 560px;
  max-width: calc(100vw - 40px);
  box-sizing: border-box;
`

const Title = styled(FormTitle)`
  font-weight: 600;
`

const Description = styled(SmallBodyText)`
  font-size: 13px;
  line-height: 1.45;
  color: ${SECONDARY_TEXT};
`

const controlLabelText = css`
  .MuiTypography-root {
    font-size: 14px;
    line-height: 1.4;
    padding-top: 0;
  }
`

const OptionLabel = styled(FormControlLabel)`
  ${controlLabelText};
  align-items: center;
  margin: 0 0 0 -9px;
`

const ScopeOption = styled(StyledFormControlLabel)`
  ${controlLabelText};
  align-items: center;
  margin: 0 0 2px -9px;
`

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 6px;

  .required {
    color: ${theme.colors.error};
    margin-left: 3px;
  }
`

const Input = styled(ControlledInput)`
  width: 100%;
  height: 34px;
  padding: 0 12px;
`

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 18px;
  margin: -8px 0;
`

const ScopeLabel = styled(FieldLabel.withComponent('span'))`
  margin-bottom: 0;
`

// Collects text, reason, matching options, and redaction scope.
function RedactAllOccurrencesDialog({
  open,
  action = REDACTION_OCCURRENCE_ACTION.REDACT,
  categoryName,
  requestScope = null,
  initialText = '',
  initialReason = '',
  busy = false,
  error = null,
  onCancel,
  onConfirm
}) {
  const isUnredacting = action === REDACTION_OCCURRENCE_ACTION.UNREDACT
  const [text, setText] = useState(initialText)
  const [reason, setReason] = useState(initialReason)
  const [scope, setScope] = useState('category')
  const [wholeWord, setWholeWord] = useState(true)
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) return
    setText(initialText)
    setReason(initialReason)
    setScope('category')
    setWholeWord(true)
    setCaseSensitive(false)
    setSubmitted(false)
  }, [open, initialText, initialReason])

  const trimmedText = text.trim()
  const textError =
    trimmedText.length < MIN_SEARCH_TEXT_LENGTH
      ? `Enter at least ${MIN_SEARCH_TEXT_LENGTH} characters to search for.`
      : trimmedText.length > MAX_SEARCH_TEXT_LENGTH
        ? `Keep the text under ${MAX_SEARCH_TEXT_LENGTH} characters.`
        : null
  const reasonError =
    !isUnredacting && reason.trim().length === 0 ? 'A reason is required — it is printed in the reasons list when redactions are applied.' : null

  function submit(e) {
    e?.preventDefault?.()
    setSubmitted(true)
    if (textError || reasonError || busy) return
    onConfirm({ action, text: trimmedText, ...(!isUnredacting ? { reason: reason.trim() } : {}), scope, caseSensitive, wholeWord })
  }

  const scopeCount = requestScope && `${requestScope.inScope} of ${requestScope.total}`
  const requestLabel = `Whole request — all categories at redactions noted${scopeCount ? ` (${scopeCount})` : ''}`
  const description = isUnredacting
    ? 'Finds every visible occurrence of the text and removes the matching area from its redaction. The file is saved immediately.'
    : 'Finds every visible occurrence of the text in the redactions-noted PDF and adds a box with the reason below. ' +
      'Existing boxes are kept; the boxes are added to the file and saved immediately.'

  return (
    <StyledDialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby="redact-all-occurrences-title" maxWidth={false}>
      <Content as="form" onSubmit={submit} noValidate data-testid="redact-all-dialog">
        <Title id="redact-all-occurrences-title">{isUnredacting ? 'Un-redact all occurrences' : 'Redact all occurrences'}</Title>
        <Description>{description}</Description>

        {isUnredacting && (
          <InfoMessage>Only the matched text will be un-redacted. Any surrounding text in the same redaction will remain redacted.</InfoMessage>
        )}

        <div>
          <FieldLabel htmlFor="redact-all-text">
            Text to {isUnredacting ? 'un-redact' : 'redact'}
            <span className="required">*</span>
          </FieldLabel>
          <Input
            id="redact-all-text"
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={MAX_SEARCH_TEXT_LENGTH + 20}
            disabled={busy}
            autoFocus
            aria-invalid={submitted && Boolean(textError)}
          />
          {submitted && textError && <ErrorText role="alert">{textError}</ErrorText>}
        </div>

        <Options>
          <OptionLabel
            control={<Checkbox checked={wholeWord} onChange={e => setWholeWord(e.target.checked)} disabled={busy} />}
            label="Match whole words only"
          />
          <OptionLabel
            control={<Checkbox checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} disabled={busy} />}
            label="Match case"
          />
        </Options>

        {!isUnredacting && (
          <div>
            <FieldLabel htmlFor="redact-all-reason">
              Reason (free text)<span className="required">*</span>
            </FieldLabel>
            <Input
              id="redact-all-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={MAX_REASON_LENGTH}
              disabled={busy}
              placeholder={getRedactionReasonPlaceholder('Third-party personal information')}
              aria-invalid={submitted && Boolean(reasonError)}
            />
            {submitted && reasonError && <ErrorText role="alert">{reasonError}</ErrorText>}
          </div>
        )}

        <div>
          <ScopeLabel id="redact-all-scope-label">Scope</ScopeLabel>
          <RadioGroup aria-labelledby="redact-all-scope-label" value={scope} onChange={e => setScope(e.target.value)} name="redact-all-scope">
            <ScopeOption value="category" control={<StyledRadio disabled={busy} />} label={`This category — ${categoryName} (1 file)`} />
            <ScopeOption value="request" control={<StyledRadio disabled={busy} />} label={requestLabel} />
          </RadioGroup>
        </div>

        {error && <ErrorText role="alert">{error}</ErrorText>}

        <DialogActionsRow>
          <SecondaryButton type="button" onClick={onCancel} disabled={busy} padding="short">
            Cancel
          </SecondaryButton>
          <Button type="submit" loading={busy} padding="short" data-testid="redact-all-confirm">
            <FindReplaceOutlinedIcon /> Find &amp; {isUnredacting ? 'un-redact' : 'redact'}
          </Button>
        </DialogActionsRow>
      </Content>
    </StyledDialog>
  )
}

RedactAllOccurrencesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  action: PropTypes.oneOf(REDACTION_OCCURRENCE_ACTIONS),
  categoryName: PropTypes.string.isRequired,
  requestScope: PropTypes.shape({ inScope: PropTypes.number.isRequired, total: PropTypes.number.isRequired }),
  initialText: PropTypes.string,
  initialReason: PropTypes.string,
  busy: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
}

export default RedactAllOccurrencesDialog
